import { useState, useEffect } from 'react';
import { useAuthStore } from '@/lib/store/authStore';
import {
  CP_TOKEN_KEY,
  decodeJwtPayload,
} from '@/lib/api/controlPlaneClient';

export type AuthSyncEventType = 'logout' | 'account-switch' | 'token-refresh';

export interface AuthSyncEvent {
  type: AuthSyncEventType;
  timestamp: number;
}

/**
 * Cross-tab auth synchronization hook.
 *
 * Listens for `storage` events on the Control Plane JWT localStorage key.
 * When another tab changes the JWT, this hook classifies the change as:
 * - `logout`: token was removed
 * - `account-switch`: token belongs to a different user
 * - `token-refresh`: same user, new token (routine refresh)
 *
 * The `storage` event only fires in OTHER tabs (not the writing tab).
 * This hook is always mounted in App.tsx but guards against pre-hydration state.
 */
export function useAuthSync(): {
  authSyncEvent: AuthSyncEvent | null;
  clearAuthSyncEvent: () => void;
} {
  const [authSyncEvent, setAuthSyncEvent] = useState<AuthSyncEvent | null>(null);

  useEffect(() => {
    function handleStorageChange(event: StorageEvent) {
      // Only react to the JWT key — it's the authoritative auth signal
      if (event.key !== CP_TOKEN_KEY) return;

      // Guard: don't classify before Zustand has hydrated
      const state = useAuthStore.getState();
      if (!state.hasHydrated) return;

      // Guard: in direct auth mode, CP keys are irrelevant
      if (state.authMode !== 'control_plane') return;

      const newToken = event.newValue;

      if (newToken === null) {
        // Token was removed — logout in another tab
        if (import.meta.env.DEV) {
          console.log('[useAuthSync] Detected: logout (token cleared in another tab)');
        }
        setAuthSyncEvent({ type: 'logout', timestamp: Date.now() });
        return;
      }

      // Token changed — decode to check identity
      const payload = decodeJwtPayload(newToken);
      const newUserId = payload
        ? (payload.user as string) ?? (payload.sub as string) ?? (payload.email as string) ?? null
        : null;

      if (newUserId === null) {
        // Can't decode user identity — treat as logout (corrupted token)
        if (import.meta.env.DEV) {
          console.log('[useAuthSync] Detected: logout (token undecodable)');
        }
        setAuthSyncEvent({ type: 'logout', timestamp: Date.now() });
        return;
      }

      const currentUsername = state.username;

      if (newUserId !== currentUsername) {
        // Different user — account switch
        if (import.meta.env.DEV) {
          console.log(
            `[useAuthSync] Detected: account-switch (${currentUsername} → ${newUserId})`
          );
        }
        setAuthSyncEvent({ type: 'account-switch', timestamp: Date.now() });
      } else {
        // Same user — token refresh
        if (import.meta.env.DEV) {
          console.log('[useAuthSync] Detected: token-refresh (same user)');
        }
        setAuthSyncEvent({ type: 'token-refresh', timestamp: Date.now() });
      }
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const clearAuthSyncEvent = () => setAuthSyncEvent(null);

  return { authSyncEvent, clearAuthSyncEvent };
}
